import { action, makeObservable, observable, runInAction } from "mobx";
import AccountManagementAPI from "../api/AccountManagementAPI";
import {
  AccountRow,
  CategoryNode,
  QuarterOrdersResult,
  SalesPocOption,
} from "../../../models/Dashboard/AccountManagement/AccountManagement";
import {
  AccountManagementFilters,
  defaultAccountManagementFilters,
} from "../../../models/Dashboard/AccountManagement/AccountManagementFilters";

export type QuarterDrawerContext = {
  customer_id: number;
  account_name: string;
  fy_year: number;
  quarter: number;
};

export class AccountManagementStore {
  accountManagementAPI: AccountManagementAPI;
  loading = false;
  filtersLoading = false;
  filters: AccountManagementFilters = defaultAccountManagementFilters();
  salesPocs: SalesPocOption[] = [];
  categories: CategoryNode[] = [];
  accounts: AccountRow[] = [];
  totalCount = 0;
  totalPages = 0;
  limit = 30;
  pageNo = 1;

  quarterDrawerOpen = false;
  quarterDrawerLoading = false;
  quarterDrawerContext: QuarterDrawerContext | null = null;
  quarterOrders: QuarterOrdersResult | null = null;

  constructor() {
    this.accountManagementAPI = new AccountManagementAPI();
    makeObservable(this, {
      loading: observable,
      filtersLoading: observable,
      filters: observable,
      salesPocs: observable,
      categories: observable,
      accounts: observable,
      totalCount: observable,
      totalPages: observable,
      limit: observable,
      pageNo: observable,
      quarterDrawerOpen: observable,
      quarterDrawerLoading: observable,
      quarterDrawerContext: observable,
      quarterOrders: observable,
      setLoading: action,
      setFiltersLoading: action,
      setFilters: action,
      updateFilters: action,
      resetFilters: action,
      fetchFilterOptions: action,
      fetchAccounts: action,
      openQuarterDrawer: action,
      closeQuarterDrawer: action,
      fetchQuarterOrders: action,
    });
  }

  setLoading = (loading: boolean) => {
    this.loading = loading;
  };

  setFiltersLoading = (loading: boolean) => {
    this.filtersLoading = loading;
  };

  setFilters = (filters: AccountManagementFilters) => {
    this.filters = filters;
  };

  updateFilters = (
    updater: (prev: AccountManagementFilters) => AccountManagementFilters,
  ) => {
    this.filters = updater(this.filters);
  };

  resetFilters = () => {
    this.filters = defaultAccountManagementFilters();
  };

  fetchFilterOptions = async () => {
    try {
      this.setFiltersLoading(true);
      const [salesPocs, categories] = await Promise.all([
        this.accountManagementAPI.getSalesPocs(),
        this.accountManagementAPI.getCategories(),
      ]);

      runInAction(() => {
        this.salesPocs = salesPocs;
        this.categories = categories;
      });
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      runInAction(() => {
        this.filtersLoading = false;
      });
    }
  };

  fetchAccounts = async (filters: AccountManagementFilters) => {
    try {
      const result = await this.accountManagementAPI.getAccounts(filters);

      runInAction(() => {
        this.accounts = result?.list ?? [];
        this.totalCount = result?.total_count ?? 0;
        this.totalPages = result?.total_pages ?? 0;
        this.limit = result?.limit ?? filters.limit;
        this.pageNo = result?.page_no ?? filters.page_no;
        this.filters = filters;
      });
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  openQuarterDrawer = (context: QuarterDrawerContext) => {
    this.quarterDrawerContext = context;
    this.quarterDrawerOpen = true;
    this.quarterOrders = null;
  };

  closeQuarterDrawer = () => {
    this.quarterDrawerOpen = false;
    this.quarterDrawerContext = null;
    this.quarterOrders = null;
    this.quarterDrawerLoading = false;
  };

  fetchQuarterOrders = async () => {
    if (!this.quarterDrawerContext) return;

    try {
      this.quarterDrawerLoading = true;
      const result = await this.accountManagementAPI.getQuarterOrders({
        customer_id: this.quarterDrawerContext.customer_id,
        fy_year: this.quarterDrawerContext.fy_year,
        quarter: this.quarterDrawerContext.quarter,
      });

      runInAction(() => {
        this.quarterOrders = result;
      });
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      runInAction(() => {
        this.quarterDrawerLoading = false;
      });
    }
  };
}
